import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeInfo,
  Ban,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CheckCircle2,
  Clock3,
  FlaskConical,
  Gauge,
  Layers3,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  X,
} from "lucide-react";

const REMEDIATION_DISCLAIMER_STORAGE_KEY = "octopus.remediation.disclaimerSeen";

function getDisclaimerText() {
  return "Assistant may be inaccurate or incomplete. Don't always trust AI.";
}

function getValue(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  return value;
}

function getBandVariant(value) {
  const v = String(value || "").toLowerCase();
  if (v === "critical") return "critical";
  if (v === "high") return "high";
  if (v === "medium") return "medium";
  if (v === "low") return "low";
  return "";
}

function Badge({ label, value, active = false, variant = "", metric = "", icon: Icon, summary, detail }) {
  return (
    <div className={`oct-impact-badge${active ? " is-active" : ""}${variant ? ` oct-impact-badge--${variant}` : ""}${metric ? ` oct-impact-badge--${metric}` : ""}`}>
      <div className="oct-impact-badge__label">
        {Icon ? <span className="oct-impact-badge__icon"><Icon size={17} /></span> : null}
        <span className="oct-impact-badge__label-text">{label}</span>
        <span className="oct-impact-badge__help" tabIndex="0" aria-label={`More information about ${label}`}>
          <BadgeInfo size={15} />
          <span className="oct-impact-badge__tooltip" role="tooltip">
            <strong>{summary}</strong>
            <span>{detail}</span>
          </span>
        </span>
      </div>
      <strong>{getValue(value)}</strong>
    </div>
  );
}

function ListBlock({ title, items = [] }) {
  return (
    <div className="oct-impact-list">
      <h4>
        <BadgeInfo size={16} />
        {title}
      </h4>
      {items.length ? (
        <ul>
          {items.map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      ) : (
        <p>N/A</p>
      )}
    </div>
  );
}

function ImpactAnalysisSkeleton() {
  return (
    <div className="oct-impact-skeleton" role="status" aria-label="Running impact analysis">
      <div className="oct-impact-skeleton__scorecard">
        {[0, 1, 2].map((item) => (
          <div className="oct-impact-skeleton__card" key={`score-${item}`}>
            <span className="oct-impact-skeleton__icon" />
            <div>
              <span className="oct-impact-skeleton__line oct-impact-skeleton__line--label" />
              <span className="oct-impact-skeleton__line oct-impact-skeleton__line--value" />
            </div>
          </div>
        ))}
      </div>
      <div className="oct-impact-skeleton__grid">
        {[0, 1, 2].map((item) => (
          <div className="oct-impact-skeleton__card" key={`detail-${item}`}>
            <span className="oct-impact-skeleton__icon" />
            <div>
              <span className="oct-impact-skeleton__line oct-impact-skeleton__line--label" />
              <span className="oct-impact-skeleton__line oct-impact-skeleton__line--value" />
            </div>
          </div>
        ))}
      </div>
      <div className="oct-impact-skeleton__panel">
        <span className="oct-impact-skeleton__line oct-impact-skeleton__line--heading" />
        <span className="oct-impact-skeleton__line" />
        <span className="oct-impact-skeleton__line oct-impact-skeleton__line--long" />
      </div>
      <div className="oct-impact-skeleton__script">
        <span className="oct-impact-skeleton__line oct-impact-skeleton__line--heading" />
      </div>
      <div className="oct-impact-skeleton__columns">
        {[0, 1, 2, 3].map((item) => (
          <div className="oct-impact-skeleton__panel" key={`column-${item}`}>
            <span className="oct-impact-skeleton__line oct-impact-skeleton__line--heading" />
            <span className="oct-impact-skeleton__line" />
            <span className="oct-impact-skeleton__line oct-impact-skeleton__line--short" />
          </div>
        ))}
      </div>
    </div>
  );
}

function toBulletList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") return value.split("\n").map((item) => item.trim()).filter(Boolean);
  return [];
}

function getErrorMessage(error) {
  const raw = String(error?.message || error || "");
  if (raw.includes("521")) {
    return "Impact analysis service is temporarily unavailable. The upstream connector returned 521 (application offline). Please retry shortly.";
  }
  return raw || "Impact analysis failed";
}

function generateFallbackRollback(rule, device) {
  const ruleName = (rule?.name || rule?.ruleName || "").toLowerCase();
  const script = String(rule?.remediationScript || "").toLowerCase();
  const deviceType = (device?.deviceType?.name || device?.deviceType || device?.type || "").toLowerCase();

  const steps = [];

  steps.push("Take a full configuration backup before applying the remediation.");

  if (/ssh/i.test(ruleName)) {
    steps.push("Re-enable the previous SSH version settings (e.g. allow SSHv1 if it was previously permitted).");
    steps.push("Verify management access via console or HTTPS before applying SSH-related changes.");
  }
  if (/password|auth|credential/i.test(ruleName)) {
    steps.push("Restore previous authentication settings from the configuration backup.");
  }
  if (/firewall|acl|access.list|policy/i.test(ruleName)) {
    steps.push("Re-apply previous ACL or firewall policy rules from the saved backup.");
  }
  if (/snmp/i.test(ruleName)) {
    steps.push("Restore previous SNMP community strings and access control entries.");
  }
  if (/ntp|time/i.test(ruleName)) {
    steps.push("Restore previous NTP server configuration.");
  }
  if (/logging|syslog/i.test(ruleName)) {
    steps.push("Restore previous syslog and logging configuration.");
  }
  if (/telnet/i.test(ruleName)) {
    steps.push("Re-enable Telnet access if it was required before this change.");
  }
  if (/http|https|web.manage/i.test(ruleName)) {
    steps.push("Restore previous HTTP/HTTPS management access configuration.");
  }
  if (/banner|motd/i.test(ruleName)) {
    steps.push("Restore previous login banner or MOTD text from backup.");
  }
  if (/encrypt|cipher|tls|ssl/i.test(ruleName)) {
    steps.push("Revert cipher suite or encryption settings to the previously configured values.");
  }

  if (/\bno\b /i.test(script) || /^no /im.test(script)) {
    steps.push("Re-add configuration lines that were removed (those prefixed with 'no') to restore the original state.");
  }
  if (/\bset\b/.test(script) && /\bconfig\b/.test(script)) {
    steps.push("Restore original parameter values using 'set' commands with the values captured in the pre-change backup.");
  }
  if (/\benable\b/.test(script)) {
    steps.push("Reverse any 'enable' commands to 'disable' where the original state was disabled.");
  }

  if (/fortinet|fortigate/i.test(deviceType)) {
    steps.push("Use FortiGate console port or HTTPS GUI to restore configuration if CLI/SSH access is disrupted.");
    steps.push("Run 'execute restore config' from FortiGate console to load the pre-change backup if needed.");
  } else if (/cisco|ios|nx.?os/i.test(deviceType)) {
    steps.push("Use console or out-of-band management port to restore the running configuration.");
    steps.push("Issue 'configure replace' with the saved backup file to roll back Cisco IOS configuration.");
  } else if (/juniper|junos/i.test(deviceType)) {
    steps.push("Run 'rollback 1' in Junos configuration mode to revert to the previous committed configuration.");
  } else if (/palo.?alto|pan.?os/i.test(deviceType)) {
    steps.push("Use PAN-OS 'revert to last saved configuration' option in the GUI or via CLI.");
  } else if (/checkpoint/i.test(deviceType)) {
    steps.push("Use SmartConsole to revert to the previous policy revision.");
  } else {
    steps.push("Use console or out-of-band access to restore configuration if remote access is disrupted.");
  }

  steps.push("Verify device connectivity and confirm management access after the rollback is complete.");

  return [...new Set(steps)];
}

function ImpactAnalysisPopup({ device, rule, result, loading, error, serviceAvailable, onReassess, onProceed, onClose, suspendClose = false }) {
  const [showDisclaimer, setShowDisclaimer] = useState(() => localStorage.getItem(REMEDIATION_DISCLAIMER_STORAGE_KEY) !== "true");
  const [showContextDetails, setShowContextDetails] = useState(false);
  const [showScript, setShowScript] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => event.key === "Escape" && !suspendClose && onClose();
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, suspendClose]);

  const dismissDisclaimer = () => {
    localStorage.setItem(REMEDIATION_DISCLAIMER_STORAGE_KEY, "true");
    setShowDisclaimer(false);
  };

  const deviceName = device?.hostName || device?.name || "N/A";
  const deviceIp = device?.mngtIpAddress || device?.ipAddress || device?.ip || "N/A";
  const deviceType = device?.deviceType?.name || device?.deviceType || device?.type || "N/A";
  const baseline = device?.msbName || device?.baselineName || device?.complianceBaseline || "N/A";
  const ruleName = rule?.name || rule?.ruleName || rule?.rule || "N/A";
  const ruleDescription = rule?.remediationDesc || rule?.description || "N/A";
  const severity = rule?.severity || "N/A";
  const script = rule?.remediationScript || "N/A";
  const scriptLines = script !== "N/A" ? String(script).split("\n").filter((line) => line.trim().length > 0).length : 0;
  const assessmentUsable = !result || result?.assessmentStatus !== "unavailable";

  const rollbackItems = useMemo(() => {
    const fromApi = toBulletList(result?.rollbackOptions);
    if (fromApi.length) return fromApi;
    return generateFallbackRollback(rule, device);
  }, [result, rule, device]);

  const fieldDetails = useMemo(() => {
    const band = result?.band || result?.impactBand || result?.impact_band;
    const impactScore = result?.impactScore ?? result?.impact_score;
    const confidence = result?.confidenceScore ?? result?.confidence_score;
    const approverRequired = result?.approverRequired ?? result?.approver_required;
    const simulatable = result?.canBeSimulated ?? result?.simulatable ?? result?.can_be_simulated;
    const execution = result?.recommendedExecutionTime || result?.execution || result?.recommended_execution_time;

    return [
      {
        key: "impact-score",
        icon: Gauge,
        label: "Impact Score",
        value: impactScore != null ? `${impactScore}/100` : null,
        variant: "",
        summary: "A numeric score that estimates the expected impact of the remediation.",
        detail: `Score ${getValue(impactScore)}/100 reflects the model's estimate of how disruptive this change may be.`,
      },
      {
        key: "band",
        icon: ShieldCheck,
        label: "Risk Band",
        value: band,
        variant: getBandVariant(band),
        summary: "A risk bucket for the score.",
        detail: `Band ${getValue(band)} groups the score into a higher-level risk category.`,
      },
      {
        key: "confidence",
        icon: Sparkles,
        label: "Confidence",
        value: confidence != null ? `${confidence}/100` : null,
        variant: "",
        summary: "How confident the model is in the assessment.",
        detail: `Confidence ${getValue(confidence)}/100 indicates how certain the model is about this result.`,
      },
      {
        key: "approve",
        icon: approverRequired ? ShieldAlert : CheckCircle2,
        label: "Approval Required",
        value: approverRequired == null ? "N/A" : approverRequired ? "Yes" : "No",
        variant: approverRequired ? "high" : "low",
        summary: "Whether human approval is required before remediation.",
        detail: approverRequired == null
          ? "The assessment did not provide an approval requirement."
          : `Human approval is ${approverRequired ? "required" : "not required"} before execution.`,
      },
      {
        key: "simulatable",
        icon: simulatable ? FlaskConical : Ban,
        label: "Simulatable",
        value: simulatable ? "Yes" : "No",
        variant: simulatable ? "low" : "high",
        summary: "Whether the action can be safely simulated first.",
        detail: `Simulatable is ${simulatable ? "Yes" : "No"} meaning the remediation ${simulatable ? "can" : "likely cannot"} be previewed before applying.`,
      },
      {
        key: "execution",
        icon: Clock3,
        label: "Execution Window",
        value: execution,
        variant: "",
        summary: "Operational guidance for applying the remediation.",
        detail: getValue(execution),
      },
    ];
  }, [result]);

  return (
    <div className="oct-impact-modal modal-v2-root" role="dialog" aria-modal="true" aria-labelledby="oct-impact-title">
      <div className="oct-impact-modal__backdrop" onClick={onClose} />
      <div className="oct-impact-modal__dialog modal-v2-shell" onClick={(e) => e.stopPropagation()}>
        <div className="oct-impact-modal__header modal-v2-header">
          <div className="oct-impact-modal__header-copy">
            <h2 id="oct-impact-title">Impact Analysis for Device {deviceName}</h2>
            <p>Review the assessed impact for <strong>{ruleName}</strong> before remediation.</p>
          </div>
          <button type="button" className="oct-impact-modal__close modal-v2-close" onClick={onClose} aria-label="Close impact analysis">
            <X size={18} />
          </button>
        </div>

        {showDisclaimer ? (
          <div className="oct-impact-modal__disclaimer">
            <ShieldAlert size={16} />
            <span>{getDisclaimerText()}</span>
            <button
              type="button"
              className="oct-impact-modal__disclaimer-close"
              onClick={dismissDisclaimer}
              aria-label="Dismiss remediation disclaimer"
            >
              <X size={14} />
            </button>
          </div>
        ) : null}

        <div className="oct-impact-modal__body modal-v2-body">
          <div className="oct-impact-modal__details">
            <button
              type="button"
              className="oct-impact-modal__expand"
              onClick={() => setShowContextDetails((value) => !value)}
              aria-expanded={showContextDetails}
            >
              <span>Device and rule details</span>
              {showContextDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
          <div
            className={`oct-impact-context-collapse${showContextDetails ? " is-open" : ""}`}
            aria-hidden={!showContextDetails}
          >
            <div className="oct-impact-context-section">
              <div className="oct-impact-context-section__heading">
                <span className="oct-impact-context-section__icon"><Layers3 size={20} /></span>
                <h3>Device and rule context</h3>
              </div>
              <div className="oct-impact-modal__inline-context">
              <div className="oct-impact-context-field">
                <div className="oct-impact-context-field__label">Device</div>
                <div className="oct-impact-context-field__value">{deviceName}</div>
              </div>
              <div className="oct-impact-context-field">
                <div className="oct-impact-context-field__label">IP Address</div>
                <div className="oct-impact-context-field__value">{deviceIp}</div>
              </div>
              <div className="oct-impact-context-field">
                <div className="oct-impact-context-field__label">Device Type</div>
                <div className="oct-impact-context-field__value">{deviceType}</div>
              </div>
              <div className="oct-impact-context-field">
                <div className="oct-impact-context-field__label">Baseline</div>
                <div className="oct-impact-context-field__value">{baseline}</div>
              </div>
              <div className="oct-impact-context-field oct-impact-context-field--wide">
                <div className="oct-impact-context-field__label">Rule</div>
                <div className="oct-impact-context-field__value">{ruleName}</div>
              </div>
              <div className="oct-impact-context-field">
                <div className="oct-impact-context-field__label">Severity</div>
                <div className="oct-impact-context-field__value">{severity}</div>
              </div>
              <div className="oct-impact-context-field oct-impact-context-field--wide">
                <div className="oct-impact-context-field__label">Description</div>
                <div className="oct-impact-context-field__value">{ruleDescription}</div>
              </div>
              </div>
            </div>
          </div>
          {loading ? (
            <ImpactAnalysisSkeleton />
          ) : error ? (
            <div className="oct-impact-error">{getErrorMessage(error)}</div>
          ) : result ? (
            <>
              <div className="oct-impact-scorecard">
                {fieldDetails.slice(0, 3).map((field) => (
                  <div key={field.key}>
                    <Badge
                      label={field.label}
                      value={field.value}
                      variant={field.variant}
                      metric={field.key}
                      icon={field.icon}
                      summary={field.summary}
                      detail={field.detail}
                    />
                  </div>
                ))}
              </div>

              <div className="oct-impact-grid">
                {fieldDetails.slice(3).map((field) => (
                  <div key={field.key} className="oct-impact-grid__cell">
                    <Badge
                      label={field.label}
                      value={field.value}
                      variant={field.variant}
                      metric={field.key}
                      icon={field.icon}
                      summary={field.summary}
                      detail={field.detail}
                    />
                  </div>
                ))}
              </div>

              <div className="oct-impact-summary">
                <h4><BadgeInfo size={16} /> Explanation</h4>
                <p>{result.explanation}</p>
              </div>

              <div className="oct-impact-script-panel">
                <button
                  type="button"
                  className="oct-impact-script-panel__toggle"
                  onClick={() => setShowScript((value) => !value)}
                  aria-expanded={showScript}
                >
                  <span>
                    <BadgeInfo size={16} /> Remediation Script
                  </span>
                  <span className="oct-impact-script-panel__meta">
                    {scriptLines ? `${scriptLines} line${scriptLines === 1 ? "" : "s"}` : "N/A"}
                    {showScript ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                </button>
                {showScript ? <pre>{script}</pre> : null}
              </div>

              <div className="oct-impact-columns">
                <ListBlock
                  title="Top impact drivers"
                  items={toBulletList(result.topImpactDrivers)}
                />
                <ListBlock
                  title="Top safety drivers"
                  items={toBulletList(result.topSafetyDrivers)}
                />
                <ListBlock
                  title="Pre-checks"
                  items={toBulletList(result.requiredPreChecks)}
                />
                <ListBlock
                  title="Rollback options"
                  items={rollbackItems}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="oct-impact-modal__footer modal-footer modal-v2-footer">
          <div className="modal-v2-footer-note"></div>
          <div className="modal-v2-footer-actions">
          <button type="button" className="btn oct-impact-modal__btn oct-impact-modal__btn--secondary modal-v2-btn modal-v2-btn-outline" onClick={onClose} disabled={loading}>
            <ChevronRight size={16} className="oct-impact-modal__btn-icon--back" /> Back
          </button>
          <button type="button" className="btn oct-impact-modal__btn oct-impact-modal__btn--secondary modal-v2-btn modal-v2-btn-outline" onClick={onReassess} disabled={loading}>
            <RefreshCw size={16} /> Reassess
          </button>
          <button type="button" className="btn oct-impact-modal__btn oct-impact-modal__btn--primary modal-v2-btn modal-v2-btn-primary" onClick={onProceed} disabled={loading || !!error || !serviceAvailable || !assessmentUsable}>
            <ArrowRight size={16} /> Proceed to Remediation
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImpactAnalysisPopup;
